import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Edit, Trash2, Package } from "lucide-react";
import { returnsAPI, getApiErrorPayload } from "@/lib/api";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loader";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { confirmAction } from "@/components/ui/confirm-dialog";
import { formatDate } from "@/lib/utils";

interface ReturnItem {
	id: string;
	productId: string;
	productName: string;
	productImage?: string;
	quantity: number;
	unitPrice: number;
	discountPercentage: number;
	discountAmount: number;
	taxPercentage: number;
	taxAmount: number;
	totalAmount: number;
}

interface ReturnTransaction {
	id: string;
	returnNumber: string;
	supplierId: string;
	supplier?: {
		id: string;
		name: string;
		email: string;
		phone: string;
		address: string;
	};
	items: ReturnItem[];
	subtotal: number;
	discountAmount: number;
	taxAmount: number;
	totalAmount: number;
	paidAmount: number;
	remainingAmount: number;
	paymentStatus: "pending" | "partial" | "paid";
	status: "pending" | "approved" | "rejected" | "completed";
	notes: string;
	returnDate: string;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
}

const ReturnDetailPage: React.FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { toast } = useToast();
	const currency = useCurrency();
	const [loading, setLoading] = useState(false);
	const [returnTransaction, setReturnTransaction] = useState<ReturnTransaction | null>(null);
	const { data: companyProfile } = useCompanyProfile({ enabled: !!id });

	useEffect(() => {
		if (id) {
			fetchReturn();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on id change only
	}, [id]);

	const fetchReturn = async () => {
		try {
			setLoading(true);
			const data = await returnsAPI.getById(id!);
			setReturnTransaction(data);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("returns.errors.fetchFailed"),
				variant: "destructive",
			});
			navigate("/returns");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async () => {
		if (await confirmAction(t("returns.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				await returnsAPI.delete(id!);
				toast({
					title: t("common.success"),
					description: t("returns.messages.deleted"),
				});
				navigate("/returns");
			} catch (error) {
				const payload = getApiErrorPayload(error);
				const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
				toast({
					title: t("common.error"),
					description: errorMessage || t("common.errors.unexpected"),
					variant: "destructive",
				});
			}
		}
	};

	const statusKey = (returnTransaction?.status ?? "").toLowerCase();
	const isLocked = statusKey === "processed" || statusKey === "cancelled";

	const getStatusBadge = (status: string) => {
		const s = String(status).toLowerCase();
		const statusConfig: Record<string, { variant: string; text: string }> = {
			pending: { variant: "warning", text: t("returns.status.pending") },
			approved: { variant: "success", text: t("returns.status.approved") },
			rejected: { variant: "destructive", text: t("returns.status.rejected") },
			processed: { variant: "default", text: t("returns.statuses.PROCESSED") },
			cancelled: { variant: "destructive", text: t("returns.statuses.CANCELLED") },
		};
		const config = statusConfig[s] || { variant: "default", text: status };
		return <Badge variant={config.variant as any}>{config.text}</Badge>;
	};

	const getPaymentStatusBadge = (status: string) => {
		const s = String(status).toLowerCase();
		const statusConfig: Record<string, { variant: string; text: string }> = {
			pending: { variant: "warning", text: t("returns.paymentStatus.pending") },
			partial: { variant: "default", text: t("returns.paymentStatus.partial") },
			paid: { variant: "success", text: t("returns.paymentStatus.paid") },
			completed: { variant: "success", text: t("returns.paymentStatus.paid") },
			refunded: { variant: "default", text: t("returns.paymentStatus.refunded") },
		};
		const config = statusConfig[s] || { variant: "default", text: status };
		return <Badge variant={config.variant as any}>{config.text}</Badge>;
	};

	if (loading) return <Loading />;

	if (!returnTransaction) {
		return (
			<div className="text-center py-8">
				<p className="text-muted-foreground">{t("returns.errors.notFound")}</p>
			</div>
		);
	}

	return (
		<div className="container mx-auto max-w-full space-y-4 px-3 py-4 md:px-4 md:py-5">
			<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">{t("returns.detail.title")}</PageHeading>
						<p className="text-muted-foreground">
							{t("returns.detail.description")} #{returnTransaction.returnNumber}
						</p>
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{!isLocked && (
						<PermissionGate permission="returns.update">
							<Button variant="outline" onClick={() => navigate(`/returns/${id}/edit`)}>
								<Edit className="h-4 w-4 mx-2" />
								{t("common.edit")}
							</Button>
						</PermissionGate>
					)}
					{isLocked && (
						<p className="text-sm text-muted-foreground">
							{t("returns.detail.processedReadOnly", "Processed or cancelled returns cannot be edited.")}
						</p>
					)}
					<PermissionGate permission="returns.delete">
						<Button variant="destructive" onClick={handleDelete}>
							<Trash2 className="h-4 w-4 mx-2" />
							{t("common.delete")}
						</Button>
					</PermissionGate>
				</div>
			</div>

			<div className="space-y-4">
				<Card>
					<CardHeader>
						<CardTitle className="flex flex-wrap items-center justify-between gap-3">
							{t("returns.detail.returnInfo")}
							<div className="flex items-center gap-2">
								<Label>{t("returns.detail.status")}</Label>
								{getStatusBadge(returnTransaction.status)}
								<Label>{t("returns.detail.paymentStatus")}</Label>
								{getPaymentStatusBadge(returnTransaction.paymentStatus)}
							</div>
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							<div className="space-y-2">
								<Label>{t("returns.detail.returnNumber")}</Label>
								<Input
									aria-label={t("returns.detail.returnNumber")}
									value={returnTransaction.returnNumber}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("returns.detail.returnDate")}</Label>
								<Input
									aria-label={t("returns.detail.returnDate")}
									value={formatDate(returnTransaction.returnDate)}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("returns.detail.notes")}</Label>
								<Input
									aria-label={t("returns.detail.notes")}
									value={returnTransaction.notes || ""}
									readOnly
								/>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>{t("returns.detail.items")}</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{returnTransaction.items?.map(item => (
								<div key={item.id} className="flex items-center gap-4 p-4 border rounded-lg">
									<div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
										{item.productImage ? (
											<img
												src={item.productImage}
												alt={item.productName}
												className="w-full h-full object-cover rounded-lg"
											/>
										) : (
											<Package className="h-8 w-8 text-primary" />
										)}
									</div>
									<div className="flex-1">
										<h4 className="font-medium">{item.productName}</h4>
										<div className="grid grid-cols-3 gap-4 mt-2">
											<div>
												<Label className="text-sm">{t("returns.detail.quantity")}</Label>
												<div className="text-lg font-semibold">{item.quantity}</div>
											</div>
											<div>
												<Label className="text-sm">{t("returns.detail.unitPrice")}</Label>
												<div className="text-lg font-semibold">
													<Price value={item.unitPrice} currency={currency} />
												</div>
											</div>
											<div>
												<Label className="text-sm">{t("returns.detail.total")}</Label>
												<div className="text-lg font-semibold">
													<Price value={item.totalAmount} currency={currency} />
												</div>
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>{t("returns.detail.summary")}</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							<div className="flex justify-between">
								<span>{t("returns.detail.subtotal")}</span>
								<span className="font-semibold">
									<Price value={returnTransaction.subtotal || 0} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("returns.detail.tax")}</span>
								<span className="font-semibold">
									<Price value={returnTransaction.taxAmount || 0} currency={currency} />
								</span>
							</div>
							<div className="border-t pt-4">
								<div className="flex justify-between text-lg font-bold">
									<span>{t("returns.detail.total")}</span>
									<span>
										<Price value={returnTransaction.totalAmount || 0} currency={currency} />
									</span>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardHeader>
						<CardTitle>{t("returns.detail.supplierInfo")}</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
							<div className="space-y-2">
								<Label>{t("returns.detail.supplierName")}</Label>
								<Input
									aria-label={t("returns.detail.supplierName")}
									value={returnTransaction.supplier?.name || ""}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("returns.detail.supplierEmail")}</Label>
								<Input
									aria-label={t("returns.detail.supplierEmail")}
									value={returnTransaction.supplier?.email || ""}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("returns.detail.supplierPhone")}</Label>
								<Input
									aria-label={t("returns.detail.supplierPhone")}
									value={returnTransaction.supplier?.phone || ""}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("returns.detail.supplierAddress")}</Label>
								<Input
									aria-label={t("returns.detail.supplierAddress")}
									value={returnTransaction.supplier?.address || ""}
									readOnly
								/>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardHeader>
						<CardTitle>{t("returns.detail.companyInfo")}</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						{companyProfile && (
							<>
								<div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
									<div className="space-y-2">
										<Label>{t("returns.detail.companyName")}</Label>
										<Input
											aria-label={t("returns.detail.companyName")}
											value={companyProfile.name}
											readOnly
										/>
									</div>
									<div className="space-y-2">
										<Label>{t("returns.detail.companyEmail")}</Label>
										<Input
											aria-label={t("returns.detail.companyEmail")}
											value={companyProfile.email}
											readOnly
										/>
									</div>
									<div className="space-y-2">
										<Label>{t("returns.detail.companyPhone")}</Label>
										<Input
											aria-label={t("returns.detail.companyPhone")}
											value={companyProfile.phone}
											readOnly
											className="rtl:text-right"
											dir="ltr"
										/>
									</div>
									<div className="space-y-2">
										<Label>{t("returns.detail.companyWebsite")}</Label>
										<Input
											aria-label={t("returns.detail.companyWebsite")}
											value={companyProfile.website}
											readOnly
										/>
									</div>
									<div className="space-y-2">
										<Label>{t("returns.detail.companyAddress")}</Label>
										<Input
											aria-label={t("returns.detail.companyAddress")}
											value={companyProfile.address}
											readOnly
										/>
									</div>
								</div>
							</>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
};

export default ReturnDetailPage;
